import React, {useRef} from 'react'
import { ExternalLink, Calendar, GraduationCap, Briefcase } from 'lucide-react';
import CustomHook from './CustomHook'
import careerJson from '../data/career.json';
import educationJson from '../data/education.json';
import '../styles/Experience.css'; // Assuming you have a CSS file for styles

const careerData = careerJson.career;
const educationData = educationJson.education;

// you can add follow under each cards in work and education
// "links": [
//                 {
//                     "name": "",
//                     "href": "",
//                     "icon": "globe"
//                 }, 
//                 {
//                     "name": "",
//                     "href": "",
//                     "icon": "github"
//                 },
//             ]

// Format date function
const formatDate = (date) => {
  if (!date) return 'Present';
  return new Date(date + ' 1').toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
};

// Timeline Item Component
const TimelineItem = ({ item, isLast }) => (
  <div className={`timeline-item ${isLast ? 'last' : ''}`}>
    <div className="timeline-card">
      {/* Header */}
      <div className="card-header">
        <div className="card-info">
          <h3>
            {item.href ? (
              <a href={item.href} target="_blank" rel="noopener noreferrer">
                {item.name}
              </a>
            ) : (
              item.name
            )}
          </h3>
          <p className="job-title">{item.title}</p>
          <div className="date-range">
            <Calendar />
            {formatDate(item.start)} - {formatDate(item.end)}
          </div>
        </div>
        
        {item.logo && (
          <div className="company-logo">
            <img src={item.logo} alt={item.name + " logo"} />
          </div>
        )}
      </div>
      
      {/* Description */}
      {item.description && (
        <div className="card-description">
          <ul>
            {item.description.map((desc, index) => (
              <li key={index}>{desc}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Links */}
      {item.links && item.links.length > 0 && (
        <div className="card-links">
          {item.links.map((link, index) => (
            <a
              key={index}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="link-item"
            >
              <ExternalLink />
              {link.name}
            </a>
          ))}
        </div>
      )}
    </div>
  </div>
);

// Timeline Component
const Timeline = ({ experience, type }) => (
  <div className="timeline">
    <div className="timeline-header">
      {type === 'work' ? <Briefcase /> : <GraduationCap />}
      <h2>{type === 'work' ? 'Work Experience' : 'Education'}</h2>
    </div>
    
    <div>
      {experience.map((item, index) => (
        <TimelineItem
          key={index}
          item={item}
          isLast={index === experience.length - 1}
        />
      ))}
    </div>
  </div>
);

// Tabs Components
const Tabs = ({ children, defaultValue }) => {
  const [activeTab, setActiveTab] = React.useState(defaultValue);
  
  return (
    <div className="tabs" data-active-tab={activeTab}>
      {React.Children.map(children, child =>
        React.cloneElement(child, { activeTab, setActiveTab })
      )}
    </div>
  );
};

const TabsList = ({ children, activeTab, setActiveTab }) => (
  <div className="tabs-list">
    {React.Children.map(children, child =>
      React.cloneElement(child, { activeTab, setActiveTab })
    )}
  </div>
);

const TabsTrigger = ({ children, value, activeTab, setActiveTab }) => (
  <button
    className={`tab-trigger ${activeTab === value ? 'active' : ''}`}
    onClick={() => setActiveTab(value)}
  >
    {children}
  </button>
);

const TabsContent = ({ children, value, activeTab }) => {
  if (activeTab !== value) return null;
  
  return (
    <div className="tab-content">
      {children}
    </div>
  );
};

// Main Experience Component
export default function Experience() {
  const refTab = useRef();
  
  CustomHook(refTab);

  return (
    <section className="experience" ref={refTab}>
      <div className="title">
        <span>Experience</span>
      </div>
      <p className="des">
        My professional journey and educational background
      </p>
      
      <Tabs defaultValue="work">
        <TabsList>
          <TabsTrigger value="work">Work</TabsTrigger>
          <TabsTrigger value="education">Education</TabsTrigger>
        </TabsList>
        
        <TabsContent value="work">
          <Timeline experience={careerData} type="work" />
        </TabsContent>
        
        <TabsContent value="education">
          <Timeline experience={educationData} type="education" />
        </TabsContent>
      </Tabs>
    </section>
  );
}